import React, { PropsWithChildren, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import Btn, { BtnModifier } from '../../../../components/molecules/Btn';
import { Icons } from '../../../../components/atoms/SVG';
import DeletableItemForm from '../../../../components/forms/DeletableItem/DeletableItemForm';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import IForm from '../../../../types/IForm';
import { StatusInfoPositiveCheck } from '../../StatusInfo/StatusInfoPositiveCheck';

export type DeletableItemMode = 'remove' | 'show';

type DeletableItemProps = PropsWithChildren<{
  id: string;
  namespace: string;
  removeForm: IForm;
  toggleMode: (mode: DeletableItemMode) => void;
  name?: string;
  position?: number;
  numberOfItems?: number;

  mode?: DeletableItemMode;
  removeItem?: () => void;
}>;

export function DeletableItem({
  removeItem,
  removeForm,
  toggleMode,
  namespace,
  children,
  mode = 'show',
  id,
  name,
  position,
  numberOfItems,
}: DeletableItemProps) {
  const [status, setStatus] = useState<InteractionStatus>(InteractionStatus.NONE);
  const { t } = useTranslation();
  const deleteButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const onSubmit = (event: any, model: any) => {
    event.preventDefault();
    setStatus(InteractionStatus.SENDING);
    model.submit().then(() => {
      setStatus(model.status);
      if (model.status === InteractionStatus.SUCCESS) {
        if (removeItem) {
          removeItem();
        }
      }
    });
  };

  const renderRemoveMode = () => {
    return (
      <>
        {status !== InteractionStatus.SUCCESS && (
          <DeletableItemForm
            id={`deletable-item-${id}-form`}
            model={removeForm}
            onSubmit={onSubmit}
            ariaLabel={t(`${namespace}.removeQuestion`, { name, position, numberOfItems })}
            cancelAction={() => toggleMode('show')}
          />
        )}
        <LiveRegion htmlTag="div" invisible={false}>
          {status === InteractionStatus.SUCCESS && (
            <div className="deletable-item-list__interaction-status">
              <StatusInfoPositiveCheck
                message={t(`${namespace}.status.${status}`) || `${namespace}.status.${status}`}
              />
            </div>
          )}
        </LiveRegion>
      </>
    );
  };

  const renderShowMode = () => {
    return (
      <div className="deletable-item-list__content">
        <div className="deletable-item-list__content-container">{children}</div>
        <div className="deletable-item-list__button">
          <Btn
            reference={deleteButtonRef}
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            svgSpriteId={Icons.DELETE_X_SMALL}
            type="button"
            text={t(`${namespace}.remove`, { name, position, numberOfItems })}
            titleText={t(`${namespace}.remove`, { name, position, numberOfItems })}
            hiddenText
            tooltipPlacement={TooltipPlacement.LEFT}
            onClick={() => toggleMode('remove')}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (mode) {
      case 'remove':
        return renderRemoveMode();
      case 'show':
      default:
        return renderShowMode();
    }
  };

  return (
    <div className="deletable-item-list__item" key={`page-${id}`}>
      {renderContent()}
    </div>
  );
}
